import { useState, useCallback } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAnalyses } from '../../hooks/useAnalyses';
import { useDashboards } from '../../hooks/useDashboards';
import { useDataExplorer } from '../../contexts/DataExplorerContext';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { BarChart3, LineChart, PieChart, Table as TableIcon, Hash, Loader2, Eye, Plus, LayoutDashboard } from 'lucide-react';
import type { Analysis } from '../../services/analysis.service';
import type { Dashboard } from '../../services/dashboard.service';
import { dashboardService } from '../../services/dashboard.service';
import { useTenantUsers } from '../../hooks/useTenantUsers';
import { UserAvatar } from '../../components/UserAvatar';
import { AnalyticsViewToggle, type AnalyticsViewMode } from '../../components/ViewModeToggle';
import { SidebarTrigger } from '../../components/ui/sidebar';
import { useFeatureFlag } from '../../services/featureFlag.service';
import { FeatureFlag } from '../../services/featureFlag.service';

const CHART_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  TABLE: TableIcon,
  BAR: BarChart3,
  LINE: LineChart,
  PIE: PieChart,
  SINGLE_VALUE: Hash,
};

function getChartTypeIcon(type: string) {
  const IconComponent = CHART_TYPE_ICONS[type] || BarChart3;
  return <IconComponent className="h-4 w-4" />;
}

function formatDate(dateString?: string): string {
  if (!dateString) return 'Never';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  } catch {
    return 'Invalid date';
  }
}

export default function Analytics() {
  const isAnalysisSectionEnabled = useFeatureFlag(FeatureFlag.ACCESS_ANALYSIS_SECTION, false);
  if (!isAnalysisSectionEnabled) {
    return <Navigate to="/" replace />;
  }

  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<AnalyticsViewMode>('charts');
  const [chartsPage, setChartsPage] = useState(1);
  const [dashboardsPage, setDashboardsPage] = useState(1);
  const pageSize = 50;
  const [isCreateDashboardDialogOpen, setIsCreateDashboardDialogOpen] = useState(false);
  const [dashboardName, setDashboardName] = useState('');
  const [dashboardDescription, setDashboardDescription] = useState('');
  const [isCreatingDashboard, setIsCreatingDashboard] = useState(false);
  const { openDataExplorer } = useDataExplorer();

  // Charts data
  const { data: chartsData, isLoading: isChartsLoading, error: chartsError } = useAnalyses({
    page: chartsPage,
    pageSize,
    filters: { ephemeral: { $ne: true } },
  });

  // Dashboards data
  const { data: dashboardsData, isLoading: isDashboardsLoading, error: dashboardsError } = useDashboards({
    page: dashboardsPage,
    pageSize,
  });

  // Tenant users for Creator column
  const { data: tenantUsers } = useTenantUsers();

  // Helper to find user by ID
  const getUserById = (userId: string | undefined) => {
    if (!userId || !tenantUsers) return null;
    return tenantUsers.find((u) => u.id === userId) || null;
  };

  const charts = chartsData?.records || [];
  const chartsTotalPages = chartsData?.total_pages || 1;
  const dashboards = dashboardsData?.records || [];
  const dashboardsTotalPages = dashboardsData?.total_pages || 1;

  const handleViewChart = (chartId: string) => {
    navigate(`/analytics/charts/${chartId}`);
  };

  const handleViewDashboard = (dashboardId: string) => {
    navigate(`/analytics/dashboards/${dashboardId}`);
  };

  const handleNewChart = useCallback(() => {
    // Create a temporary analysis object without saving to database
    // The user can save it via the DataExplorer's save functionality
    const tempAnalysis: Analysis = {
      id: '',
      name: 'Untitled Chart',
      visualization_type: 'TABLE',
      dimension_ids: [],
      measure_ids: [],
      dimension_filters: [],
    };

    openDataExplorer({
      analysis: tempAnalysis,
      originalAnalysisId: '',
    });
  }, [openDataExplorer]);

  const handleCreateDashboard = async () => {
    if (!dashboardName.trim()) {
      return;
    }

    setIsCreatingDashboard(true);
    try {
      const newDashboard: Omit<Dashboard, 'id' | 'last_updated'> = {
        name: dashboardName.trim(),
        description: dashboardDescription.trim() || '',
        components: [],
        version_idx: 0,
      };

      const createdDashboard = await dashboardService.create(newDashboard);

      setIsCreateDashboardDialogOpen(false);
      setDashboardName('');
      setDashboardDescription('');

      navigate(`/analytics/dashboards/${createdDashboard.id}`);
    } catch (error) {
      console.error('Failed to create dashboard:', error);
      alert('Failed to create dashboard. Please try again.');
    } finally {
      setIsCreatingDashboard(false);
    }
  };

  const isLoading = viewMode === 'charts' ? isChartsLoading : isDashboardsLoading;
  const error = viewMode === 'charts' ? chartsError : dashboardsError;
  const page = viewMode === 'charts' ? chartsPage : dashboardsPage;
  const setPage = viewMode === 'charts' ? setChartsPage : setDashboardsPage;
  const totalPages = viewMode === 'charts' ? chartsTotalPages : dashboardsTotalPages;

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between w-full px-4 md:px-6 py-3 md:py-4 border-b border-border bg-background flex-shrink-0 z-10 gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <SidebarTrigger className="md:hidden h-5 w-5" />
          <div className="flex flex-col gap-1 md:gap-2 min-w-0">
            {/* Breadcrumbs */}
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Analytics</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            {/* Page Title */}
            <h1 className="text-lg md:text-xl font-semibold text-foreground">Analytics</h1>
          </div>
        </div>
        <div className="flex items-center gap-3 md:gap-4">
          {/* View Toggle */}
          <AnalyticsViewToggle value={viewMode} onChange={setViewMode} />
          {/* Create Button */}
          {viewMode === 'charts' ? (
            <Button
              onClick={handleNewChart}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Chart
            </Button>
          ) : (
            <Button
              onClick={() => setIsCreateDashboardDialogOpen(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Dashboard
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Loading {viewMode === 'charts' ? 'charts' : 'dashboards'}...
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">
              Failed to load {viewMode === 'charts' ? 'charts' : 'dashboards'}. Please try again.
            </p>
          </div>
        ) : viewMode === 'charts' ? (
          // Charts Table
          charts.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">No charts found. Create your first chart to get started.</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Creator</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {charts.map((chart: Analysis) => (
                      <TableRow key={chart.id}>
                        <TableCell className="font-medium">
                          {chart.name || 'Untitled Chart'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getChartTypeIcon(chart.visualization_type)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <UserAvatar
                            user={getUserById(chart.created_by)}
                            createdBy={chart.created_by}
                          />
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(chart.last_updated)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewChart(chart.id)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (page > 1) setPage(page - 1);
                          }}
                          style={{ pointerEvents: page === 1 ? 'none' : 'auto', opacity: page === 1 ? 0.5 : 1 }}
                        />
                      </PaginationItem>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= page - 1 && pageNum <= page + 1)
                        ) {
                          return (
                            <PaginationItem key={pageNum}>
                              <PaginationLink
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setPage(pageNum);
                                }}
                                isActive={pageNum === page}
                              >
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        } else if (pageNum === page - 2 || pageNum === page + 2) {
                          return (
                            <PaginationItem key={`ellipsis-${pageNum}`}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          );
                        }
                        return null;
                      })}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (page < totalPages) setPage(page + 1);
                          }}
                          style={{ pointerEvents: page === totalPages ? 'none' : 'auto', opacity: page === totalPages ? 0.5 : 1 }}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )
        ) : (
          // Dashboards Table
          dashboards.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">No dashboards found. Create your first dashboard to get started.</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Creator</TableHead>
                      <TableHead>Components</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboards.map((dashboard: Dashboard) => (
                      <TableRow key={dashboard.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
                            {dashboard.name || 'Untitled Dashboard'}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {dashboard.description || '-'}
                        </TableCell>
                        <TableCell>
                          <UserAvatar
                            user={getUserById(dashboard.created_by)}
                            createdBy={dashboard.created_by}
                          />
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {dashboard.components?.length || 0} component{(dashboard.components?.length || 0) !== 1 ? 's' : ''}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(dashboard.last_updated)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDashboard(dashboard.id)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (page > 1) setPage(page - 1);
                          }}
                          style={{ pointerEvents: page === 1 ? 'none' : 'auto', opacity: page === 1 ? 0.5 : 1 }}
                        />
                      </PaginationItem>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= page - 1 && pageNum <= page + 1)
                        ) {
                          return (
                            <PaginationItem key={pageNum}>
                              <PaginationLink
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setPage(pageNum);
                                }}
                                isActive={pageNum === page}
                              >
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        } else if (pageNum === page - 2 || pageNum === page + 2) {
                          return (
                            <PaginationItem key={`ellipsis-${pageNum}`}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          );
                        }
                        return null;
                      })}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (page < totalPages) setPage(page + 1);
                          }}
                          style={{ pointerEvents: page === totalPages ? 'none' : 'auto', opacity: page === totalPages ? 0.5 : 1 }}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )
        )}
      </div>

      {/* Create Dashboard Dialog */}
      <Dialog open={isCreateDashboardDialogOpen} onOpenChange={setIsCreateDashboardDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Dashboard</DialogTitle>
            <DialogDescription>
              Enter a name and optional description for your new dashboard. You can configure it after creation.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <Input
              placeholder="Dashboard name"
              value={dashboardName}
              onChange={(e) => setDashboardName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && dashboardName.trim() && !isCreatingDashboard) {
                  handleCreateDashboard();
                }
              }}
              autoFocus
            />
            <Input
              placeholder="Description (optional)"
              value={dashboardDescription}
              onChange={(e) => setDashboardDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && dashboardName.trim() && !isCreatingDashboard) {
                  handleCreateDashboard();
                }
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDashboardDialogOpen(false);
                setDashboardName('');
                setDashboardDescription('');
              }}
              disabled={isCreatingDashboard}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateDashboard}
              disabled={!dashboardName.trim() || isCreatingDashboard}
            >
              {isCreatingDashboard ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
